import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Globe, LoaderCircle, Plus, PowerOff, Rocket, ServerCrash, XCircle } from 'lucide-react';
import {
  checkDomainAvailabilityApi,
  completeDeploymentApi,
  createDeploymentApi,
  fetchDeploymentsApi
} from '../../api/sandbox.api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Tabs } from '../../components/ui/Tabs';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';
import { PublishCostConfirmModal } from './PublishCostConfirmModal';

const CUSTOM_DOMAIN_REGEX =
  /^(?=.{4,253}$)(?!-)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
const RISMAN_SUBDOMAIN_REGEX = /^(?=.{3,63}$)[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$/i;
const DOMAIN_CHECK_DEBOUNCE_MS = 500;
const DEPLOYMENT_PROGRESS_DURATION_MS = 120000;
const DEPLOYMENT_PROGRESS_TICK_MS = 200;

const normalizeDomainInput = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');

const getStatusTone = (status) => {
  const normalizedStatus = (status || '').toLowerCase();

  if (normalizedStatus === 'active') {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300';
  }

  if (normalizedStatus === 'completed') {
    return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300';
  }

  if (normalizedStatus === 'failed') {
    return 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300';
  }

  return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300';
};

const getStatusLabel = (status, translate) => {
  const normalizedStatus = (status || '').toLowerCase();

  if (normalizedStatus === 'active') {
    return translate('deploymentsStatusActive');
  }

  if (normalizedStatus === 'completed') {
    return translate('deploymentsStatusCompleted');
  }

  if (normalizedStatus === 'failed') {
    return translate('deploymentsStatusFailed');
  }

  return status || '-';
};

const formatBuildDurationSeconds = (buildDurationMs) => {
  if (typeof buildDurationMs !== 'number') {
    return '-';
  }

  return Math.round(buildDurationMs / 1000);
};

export const DeploymentsManager = ({ projectId, project = null, forceLoading = false, disableFetch = false }) => {
  const { t, formatDate } = useI18n();
  const [deployments, setDeployments] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState('current');
  const [domainMethod, setDomainMethod] = useState('risman');
  const [domainInput, setDomainInput] = useState('');
  const [domainStatus, setDomainStatus] = useState({
    status: 'idle',
    message: '',
    checkedDomain: ''
  });
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState(0);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [deactivatingId, setDeactivatingId] = useState('');
  const [deactivateError, setDeactivateError] = useState('');
  const [costConfirmOpen, setCostConfirmOpen] = useState(false);

  const activeDeployment = useMemo(
    () => deployments.find((deployment) => (deployment.status || '').toLowerCase() === 'active' && deployment.domain),
    [deployments]
  );
  const hasActiveDeployment = Boolean(activeDeployment);
  const isCurrentDomainTab = hasActiveDeployment && modalTab === 'current';

  const selectedDomain = useMemo(() => {
    const normalizedValue = normalizeDomainInput(domainInput);

    if (!normalizedValue) {
      return { domain: '', validationError: '' };
    }

    if (domainMethod === 'custom') {
      if (!CUSTOM_DOMAIN_REGEX.test(normalizedValue)) {
        return { domain: '', validationError: t('deploymentsCustomDomainInvalid') };
      }

      return { domain: normalizedValue, validationError: '' };
    }

    const rawSubdomain = normalizedValue.endsWith('.risman.pro')
      ? normalizedValue.slice(0, -'.risman.pro'.length)
      : normalizedValue;
    const normalizedSubdomain = rawSubdomain.replace(/\.+/g, '');

    if (!RISMAN_SUBDOMAIN_REGEX.test(normalizedSubdomain)) {
      return { domain: '', validationError: t('deploymentsRismanSubdomainInvalid') };
    }

    return { domain: `${normalizedSubdomain}.risman.pro`, validationError: '' };
  }, [domainInput, domainMethod, t]);

  const loadDeployments = useCallback(async () => {
    setLoadingList(true);
    setListError('');

    try {
      const items = await fetchDeploymentsApi({ projectId });
      setDeployments(items);
    } catch (error) {
      setListError(error?.message || t('deploymentsListError'));
    } finally {
      setLoadingList(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    if (!projectId) {
      setLoadingList(false);
      setDeployments([]);
      return;
    }

    if (disableFetch) {
      setLoadingList(true);
      setListError('');
      return;
    }

    loadDeployments();
  }, [projectId, loadDeployments, disableFetch]);

  useEffect(() => {
    if (!modalOpen || isCurrentDomainTab) {
      return undefined;
    }

    if (!selectedDomain.domain) {
      setDomainStatus({
        status: selectedDomain.validationError ? 'invalid' : 'idle',
        message: selectedDomain.validationError,
        checkedDomain: ''
      });
      return undefined;
    }

    let cancelled = false;
    setDomainStatus({
      status: 'checking',
      message: t('deploymentsDomainChecking'),
      checkedDomain: selectedDomain.domain
    });

    const timer = window.setTimeout(async () => {
      try {
        const result = await checkDomainAvailabilityApi({
          projectId,
          domain: selectedDomain.domain
        });

        if (cancelled) {
          return;
        }

        if (result.exists) {
          setDomainStatus({
            status: 'unavailable',
            message: t('deploymentsDomainUnavailable'),
            checkedDomain: selectedDomain.domain
          });
          return;
        }

        setDomainStatus({
          status: 'available',
          message: t('deploymentsDomainAvailable'),
          checkedDomain: selectedDomain.domain
        });
      } catch (error) {
        if (!cancelled) {
          setDomainStatus({
            status: 'error',
            message: error?.message || t('deploymentsDomainCheckError'),
            checkedDomain: selectedDomain.domain
          });
        }
      }
    }, DOMAIN_CHECK_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [modalOpen, isCurrentDomainTab, projectId, selectedDomain.domain, selectedDomain.validationError, t]);

  useEffect(() => {
    if (!creating) {
      return undefined;
    }

    const startTime = Date.now();
    setCreateProgress(0);

    const interval = window.setInterval(() => {
      const elapsedMs = Date.now() - startTime;
      const nextProgress = Math.min((elapsedMs / DEPLOYMENT_PROGRESS_DURATION_MS) * 100, 100);
      setCreateProgress(nextProgress);
    }, DEPLOYMENT_PROGRESS_TICK_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [creating]);

  const openModal = () => {
    setModalTab(hasActiveDeployment ? 'current' : 'new');
    setModalOpen(true);
  };

  const closeModal = (force = false) => {
    if (creating && !force) {
      return;
    }

    setModalOpen(false);
    setModalTab('current');
    setDomainInput('');
    setDomainMethod('risman');
    setDomainStatus({
      status: 'idle',
      message: '',
      checkedDomain: ''
    });
    setCreateError('');
    setCostConfirmOpen(false);
  };

  const handleDeactivateDeployment = async (deploymentId) => {
    if (!deploymentId || deactivatingId) {
      return;
    }

    setDeactivatingId(deploymentId);
    setDeactivateError('');

    try {
      await completeDeploymentApi({ deploymentId });
      await loadDeployments();
    } catch (error) {
      setDeactivateError(error?.message || t('deploymentsDeactivateError'));
    } finally {
      setDeactivatingId('');
    }
  };

  const handleCreateDeployment = async () => {
    const deployDomain = isCurrentDomainTab ? activeDeployment.domain : selectedDomain.domain;

    if (creating || !deployDomain) {
      return;
    }

    if (!isCurrentDomainTab && domainStatus.status !== 'available') {
      return;
    }

    setCreating(true);
    setCreateProgress(0);
    setCreateError('');
    setCreateSuccess('');
    setCostConfirmOpen(false);

    try {
      const deployment = await createDeploymentApi({
        projectId,
        domain: deployDomain,
        provider: 'Http'
      });
      setDeployments((current) => [deployment, ...current]);
      setCreateProgress(100);
      setCreateSuccess(t('deploymentsCreateSuccess'));
      closeModal(true);
      await loadDeployments();
    } catch (error) {
      setCreateError(error?.message || t('deploymentsCreateError'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeploymentFormSubmit = (event) => {
    event.preventDefault();

    if (!canSubmitDeployment || creating) {
      return;
    }

    setCostConfirmOpen(true);
  };

  const domainHint = domainMethod === 'custom' ? t('deploymentsCustomDomainHint') : t('deploymentsRismanDomainHint');
  const hasCurrentDomainStatus = Boolean(domainStatus.message) && domainStatus.checkedDomain === selectedDomain.domain;
  const canSubmitDeployment = isCurrentDomainTab
    ? !creating
    : !creating && domainStatus.status === 'available' && domainStatus.checkedDomain === selectedDomain.domain;
  const submitButtonLabel = creating
    ? t('deploymentsPublishingNow')
    : canSubmitDeployment
      ? isCurrentDomainTab
        ? t('publish')
        : t('newDeployment')
      : t('deploymentsSubmitDisabled');
  const showLoadingSkeleton = forceLoading || loadingList;
  const domainInputToneClass = hasCurrentDomainStatus
    ? domainStatus.status === 'available'
      ? 'border-emerald-400 focus:border-emerald-500 dark:border-emerald-600'
      : domainStatus.status === 'checking'
        ? 'border-neutral-400 focus:border-neutral-500 dark:border-neutral-600'
        : 'border-rose-400 focus:border-rose-500 dark:border-rose-600'
    : '';

  return (
    <>
      <section className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-neutral-950 dark:text-white">{t('deploymentsTitle')}</h2>
            <p className="mt-1 text-sm text-neutral-500">{t('deploymentsSubtitle')}</p>
          </div>
          <Button type="button" onClick={openModal}>
            <Plus size={16} />
            {t('newDeployment')}
          </Button>
        </div>

        {createSuccess ? (
          <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
            {createSuccess}
          </p>
        ) : null}

        {deactivateError ? (
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
            {deactivateError}
          </p>
        ) : null}

        {listError ? (
          <div className="space-y-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
            <p>{listError}</p>
            <Button type="button" variant="secondary" onClick={loadDeployments}>
              {t('deploymentsRetry')}
            </Button>
          </div>
        ) : null}

        {showLoadingSkeleton ? (
          <div className="space-y-3">
            {[0, 1, 2].map((item) => (
              <div key={item} className="animate-pulse rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
                <div className="flex items-center justify-between gap-3">
                  <div className="h-4 w-40 rounded bg-neutral-200 dark:bg-neutral-800" />
                  <div className="h-6 w-20 rounded-lg bg-neutral-200 dark:bg-neutral-800" />
                </div>
                <div className="mt-3 space-y-2">
                  <div className="h-3 w-10/12 rounded bg-neutral-200 dark:bg-neutral-800" />
                  <div className="h-3 w-8/12 rounded bg-neutral-200 dark:bg-neutral-800" />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {!showLoadingSkeleton && !listError && deployments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-neutral-200 bg-transparent p-8 text-center dark:border-neutral-800">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
              <Rocket size={20} />
            </div>
            <h3 className="text-sm font-semibold text-neutral-950 dark:text-white">{t('deploymentsEmptyTitle')}</h3>
            <p className="mt-2 text-sm text-neutral-500">{t('deploymentsEmptyBody')}</p>
            <Button type="button" className="mt-5" onClick={openModal}>
              <Plus size={16} />
              {t('newDeployment')}
            </Button>
          </div>
        ) : null}

        {!showLoadingSkeleton && !listError && deployments.length > 0 ? (
          <div className="space-y-3">
            {deployments.map((deployment) => (
              <article
                key={deployment.id}
                className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition hover:border-neutral-300 hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)] dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-neutral-950 dark:text-white" dir="ltr">
                    {deployment.domain}
                  </h3>
                  <span className={classNames('rounded-md px-2 py-0.5 text-xs font-medium', getStatusTone(deployment.status))}>
                    {getStatusLabel(deployment.status, t)}
                  </span>
                </div>
                <div className="mt-3 grid gap-2 text-sm text-neutral-500 sm:grid-cols-2">
                  <p>
                    {t('deploymentsCreatedAt', {
                      value: deployment.createdAt
                        ? formatDate(deployment.createdAt, { dateStyle: 'medium', timeStyle: 'short' })
                        : '-'
                    })}
                  </p>
                  <p>
                    {t('deploymentsBuildDuration', {
                      value: formatBuildDurationSeconds(deployment.buildDurationMs)
                    })}
                  </p>
                </div>
                {deployment.deployUrl ? (
                  <a
                    href={deployment.deployUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-sm font-medium text-sky-700 underline-offset-2 hover:underline dark:text-sky-300"
                  >
                    {deployment.deployUrl}
                  </a>
                ) : null}
                {deployment.error ? (
                  <p className="mt-2 text-sm text-rose-600 dark:text-rose-300">{deployment.error}</p>
                ) : null}
                {(deployment.status || '').toLowerCase() === 'active' ? (
                  <div className="mt-4 flex justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      className="px-3 py-2"
                      disabled={Boolean(deactivatingId)}
                      onClick={() => handleDeactivateDeployment(deployment.id)}
                    >
                      {deactivatingId === deployment.id ? (
                        <LoaderCircle size={16} className="animate-spin" />
                      ) : (
                        <PowerOff size={16} />
                      )}
                      {deactivatingId === deployment.id
                        ? t('deploymentsDeactivating')
                        : t('deploymentsDeactivate')}
                    </Button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        ) : null}
      </section>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        closeDisabled={creating}
        title={t('newDeployment')}
        panelClassName="max-w-2xl p-0"
        contentClassName="mb-0 gap-2 border-b border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5"
        closeButtonClassName="h-8 w-8"
      >
        <form className="space-y-2" onSubmit={handleDeploymentFormSubmit}>
          {creating ? (
            <div className="space-y-2 px-2 py-2">
              <div className="space-y-2 rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900">
                <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
                  <LoaderCircle size={16} className="animate-spin" />
                  <span>{t('deploymentsPublishingNow')}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
                  <div
                    className="h-full rounded-full bg-neutral-950 transition-[width] duration-200 ease-linear dark:bg-white"
                    style={{ width: `${createProgress}%` }}
                  />
                </div>
              </div>
              <p className="rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs leading-5 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950">
                {t('deploymentsFirstDeployDnsHint')}
              </p>
            </div>
          ) : (
            <div className="space-y-2 px-2 py-2">
              {hasActiveDeployment ? (
                <Tabs
                  className="w-full"
                  tabClassName="flex-1"
                  value={modalTab}
                  onChange={setModalTab}
                  tabs={[
                    {
                      value: 'current',
                      label: t('deploymentsCurrentDomainTab')
                    },
                    {
                      value: 'new',
                      label: t('deploymentsNewDomainTab')
                    }
                  ]}
                />
              ) : null}

              {isCurrentDomainTab ? (
                <>
                  <p className="rounded-xl border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-xs leading-5 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
                    {t('deploymentsCurrentDomainModalHint')}
                  </p>

                  <div className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-950">
                    <span className="text-sm font-semibold text-neutral-950 dark:text-white">{t('deploymentsCurrentDomainLabel')}</span>
                    <p dir="ltr" className="mt-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-left text-sm font-medium text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white">
                      {activeDeployment.domain}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="rounded-xl border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-xs leading-5 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
                    {t('deploymentsModalHint')}
                  </p>

                  <fieldset className="space-y-2">
                    <legend className="mb-1 text-sm font-semibold text-neutral-950 dark:text-white">{t('deploymentsDomainMethod')}</legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <label
                        className={classNames(
                          'cursor-pointer rounded-xl border p-2.5 transition',
                          domainMethod === 'custom'
                            ? 'border-neutral-300 bg-[#F5F5F5] dark:border-neutral-700 dark:bg-neutral-900'
                            : 'border-neutral-200 bg-white hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-900'
                        )}
                      >
                        <input
                          type="radio"
                          className="sr-only"
                          name="domain-method"
                          checked={domainMethod === 'custom'}
                          onChange={() => {
                            setDomainMethod('custom');
                            setDomainInput('');
                            setCreateError('');
                          }}
                        />
                        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-950 dark:text-white">
                          <Globe size={16} />
                          {t('deploymentsCustomDomain')}
                        </div>
                        <p className="mt-2 text-xs leading-5 text-neutral-500">{t('deploymentsCustomDomainOptionHint')}</p>
                      </label>
                      <label
                        className={classNames(
                          'cursor-pointer rounded-xl border p-2.5 transition',
                          domainMethod === 'risman'
                            ? 'border-neutral-300 bg-[#F5F5F5] dark:border-neutral-700 dark:bg-neutral-900'
                            : 'border-neutral-200 bg-white hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-900'
                        )}
                      >
                        <input
                          type="radio"
                          className="sr-only"
                          name="domain-method"
                          checked={domainMethod === 'risman'}
                          onChange={() => {
                            setDomainMethod('risman');
                            setDomainInput('');
                            setCreateError('');
                          }}
                        />
                        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-950 dark:text-white">
                          <Rocket size={16} />
                          {t('deploymentsRismanDomain')}
                        </div>
                        <p className="mt-2 text-xs leading-5 text-neutral-500">{t('deploymentsRismanDomainOptionHint')}</p>
                      </label>
                    </div>
                  </fieldset>

                  <div className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-950">
                    <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                      <span className="text-sm font-semibold text-neutral-950 dark:text-white">
                        {domainMethod === 'custom' ? t('deploymentsCustomDomainLabel') : t('deploymentsRismanSubdomainLabel')}
                      </span>
                      {hasCurrentDomainStatus ? (
                        <p
                          className={classNames(
                            'inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium',
                            domainStatus.status === 'available'
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                              : domainStatus.status === 'checking'
                                ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
                                : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
                          )}
                        >
                          {domainStatus.status === 'checking' ? <LoaderCircle size={12} className="animate-spin" /> : null}
                          {domainStatus.status === 'available' ? <CheckCircle2 size={12} /> : null}
                          {domainStatus.status === 'checking' || domainStatus.status === 'available' ? null : <XCircle size={12} />}
                          <span>{domainStatus.message}</span>
                        </p>
                      ) : null}
                    </div>

                    {domainMethod === 'custom' ? (
                      <Input
                        value={domainInput}
                        disabled={creating}
                        placeholder="my-app.example.com"
                        className={domainInputToneClass}
                        onChange={(event) => {
                          setDomainInput(event.target.value);
                          setCreateError('');
                        }}
                      />
                    ) : (
                      <label className="block space-y-2">
                        <div
                          dir="ltr"
                          className={classNames(
                            'flex items-center overflow-hidden rounded-lg border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950',
                            hasCurrentDomainStatus
                              ? domainStatus.status === 'available'
                                ? 'border-emerald-400 focus-within:border-emerald-500 dark:border-emerald-600'
                                : domainStatus.status === 'checking'
                                  ? 'border-neutral-400 focus-within:border-neutral-500 dark:border-neutral-600'
                                  : 'border-rose-400 focus-within:border-rose-500 dark:border-rose-600'
                              : 'focus-within:border-neutral-400 dark:focus-within:border-neutral-600'
                          )}
                        >
                          <input
                            type="text"
                            value={domainInput}
                            disabled={creating}
                            placeholder="landing"
                            className="min-w-0 flex-1 bg-transparent px-3 py-2 text-left text-sm text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-white"
                            onChange={(event) => {
                              setDomainInput(event.target.value);
                              setCreateError('');
                            }}
                          />
                          <span className="shrink-0 border-s border-neutral-200 bg-neutral-50 px-2.5 py-2 text-sm font-medium text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
                            .risman.pro
                          </span>
                        </div>
                      </label>
                    )}

                    <p className="mt-3 text-xs leading-5 text-neutral-500">{domainHint}</p>
                    <p className="mt-2 text-xs leading-5 text-neutral-500">{t('deploymentsDomainFormatNotice')}</p>
                    {domainMethod === 'custom' ? (
                      <p className="mt-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
                        {t('deploymentsNsHint')}
                      </p>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          )}

          {!creating ? (
            <div className="flex flex-col-reverse gap-2 border-t border-neutral-100 px-4 py-3 sm:flex-row sm:justify-end sm:px-5 dark:border-neutral-900">
              <Button type="button" variant="secondary" disabled={creating} onClick={closeModal}>
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!canSubmitDeployment}
              >
                <>
                  <ServerCrash size={16} />
                  {submitButtonLabel}
                </>
              </Button>
            </div>
          ) : null}

          {createError ? (
            <p className="mx-2 mb-2 rounded-lg border border-rose-200 bg-rose-50/80 px-2.5 py-1.5 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
              {createError}
            </p>
          ) : null}
        </form>
      </Modal>

      <PublishCostConfirmModal
        open={costConfirmOpen}
        onClose={() => setCostConfirmOpen(false)}
        onConfirm={handleCreateDeployment}
        project={project}
        confirming={creating}
      />
    </>
  );
};
